import { Suspense, useDeferredValue } from 'react'
import { createFileRoute, Link, stripSearchParams, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import * as valibot from 'valibot'
import { ArrowLeftIcon } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { SuperAdminBoundary } from './-components/admin/super-admin-boundary'
import { TraceDetailPending, TraceDetailSkeleton } from './-components/admin/admin-pending'
import {
  CopyButton,
  formatUtc,
  LINK_CLASS,
  SessionLink,
  StoreDownNotice,
  type TraceSpan,
} from './-components/admin/traces-shared'
import { traceRootLabel, TraceWaterfall } from './-components/admin/trace-waterfall'

// One trace's own page, so anything that knows a trace id (an error, a session's timeline, the explorer) can link to it.
// `?at=` centres the server's search window on the moment the linking row knows; without it, the last 7 days.

export const Route = createFileRoute('/_app/admin/traces_/$traceId')({
  validateSearch: valibot.object({
    at: valibot.optional(valibot.string()),
    wide: valibot.fallback(valibot.optional(valibot.boolean(), false), false),
    span: valibot.optional(valibot.string()),
  }),
  search: { middlewares: [stripSearchParams({ wide: false })] },
  component: RouteComponent,
  pendingComponent: TraceDetailPending,
  staticData: { fullWidth: true },
})

function RouteComponent() {
  const { traceId } = Route.useParams()
  return (
    <div className={SETTINGS_STACK}>
      <Link to="/admin/traces" className={cn(LINK_CLASS, 'flex w-fit items-center gap-1 text-sm')}>
        <ArrowLeftIcon className="size-3.5" />
        Traces
      </Link>
      <SuperAdminBoundary what="this trace">
        <Suspense fallback={<TraceDetailSkeleton header />}>
          <TraceBody traceId={traceId} />
        </Suspense>
      </SuperAdminBoundary>
    </div>
  )
}

function TraceBody(props: { traceId: string }) {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const context = useMatch({ from: '/_app/admin/traces_/$traceId/_spans' }).context
  const deferred = useDeferredValue(context.traceQueryOptions)
  const { data } = useSuspenseQuery(deferred)
  if (!data.ok) {
    return (
      <>
        <TraceHeader traceId={props.traceId} spans={[]} />
        <StoreDownNotice reason={data.reason} />
      </>
    )
  }
  return (
    <div
      className={cn('flex flex-col gap-6 transition-opacity', deferred !== context.traceQueryOptions && 'opacity-60')}
    >
      <TraceHeader traceId={props.traceId} spans={data.spans} />
      {data.spans.length === 0 ? (
        <NotFound />
      ) : (
        <div className="overflow-hidden rounded-xl border">
          <TraceWaterfall
            spans={data.spans}
            selectedSpanId={search.span}
            onSelectSpan={(span) => void navigate({ search: (prev) => ({ ...prev, span }), replace: true })}
          />
        </div>
      )}
    </div>
  )
}

function NotFound() {
  const search = Route.useSearch()
  const where = search.wide
    ? 'in the last 30 days'
    : search.at
      ? `within an hour of ${formatUtc(search.at)}`
      : 'in the last 7 days'
  return (
    <div role="status" className="flex flex-col gap-1 rounded-lg border p-4">
      <span className="text-sm font-medium">Trace not found</span>
      <span className="text-muted-foreground text-sm">
        No spans {where} carry this trace id.{' '}
        {!search.wide && (
          <Link to="." search={(prev) => ({ ...prev, wide: true })} className={LINK_CLASS}>
            Search the last 30 days
          </Link>
        )}
      </span>
    </div>
  )
}

function TraceHeader(props: { traceId: string; spans: TraceSpan[] }) {
  const spans = props.spans
  const label = traceRootLabel(spans)
  const errorCount = spans.filter((span) => span.error).length
  const startedAt = spans[0]?.occurredAt
  // Pageviews carry no session id; their API spans do.
  const sessionId = spans.find((span) => span.sessionId)?.sessionId
  const userId = spans.find((span) => span.userId)?.userId
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-2xl font-semibold tracking-tight">Trace</h2>
        {label && <span className="text-muted-foreground min-w-0 truncate font-mono text-sm">{label}</span>}
        {errorCount > 0 && <Badge variant="destructive">{`${errorCount} error${errorCount === 1 ? '' : 's'}`}</Badge>}
      </div>
      <span className="text-muted-foreground flex items-center gap-1 font-mono text-xs break-all">
        {props.traceId}
        <CopyButton value={props.traceId} label="Copy trace id" />
      </span>
      {startedAt && <span className="text-muted-foreground text-xs tabular-nums">{formatUtc(startedAt)}</span>}
      {sessionId && (
        <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
          Session
          <SessionLink sessionId={sessionId} at={startedAt} full />
        </span>
      )}
      {userId && (
        <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
          User
          <Link to="/admin/traces" search={{ user: userId }} className={cn(LINK_CLASS, 'font-mono')}>
            {userId}
          </Link>
        </span>
      )}
    </div>
  )
}
